// src/App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { CartProvider } from './contexts/CartContext';
import { BookingProvider } from './contexts/BookingContext';
import { BookAccessGuard } from './components/books/BookAccessGuard';


// Page imports
import { Login } from './components/auth/Login';
import { Signup } from './components/auth/Signup';
import { ProfilePage } from './pages/ProfilePage';
import { Dashboard } from './pages/Dashboard';
import { Home } from './pages/Home';
import { BookViewer } from './pages/BookViewer';
import { BookEditor } from './pages/BookEditor';
import { BookCreator } from './components/books/BookCreator';
import { CustomDomainBookViewer } from './components/books/CustomBookViewer';
import { OrganisationPage } from './pages/OrganisationPage';

import { NotificationProvider } from './contexts/NotificationContext';
import { NotificationBell } from './components/notifications/NotificationBell';
import { NotificationDashboard } from './components/provider/NotificationDashboard';

import { ProviderDashboard } from './components/provider/ProviderDashboard';
import { ProviderProfile } from './components/provider/ProviderProfile';
import { ProviderRequests } from './components/provider/ProviderRequests';
import { ProviderBookings } from './components/provider/ProviderBookings';

// Layout
import { Navbar } from './components/layout/Navbar';

// Firebase
import { db } from './config/firebase';
import { collection, query, getDoc, doc, where, getDocs, limit } from 'firebase/firestore';

function App() {
  const [isCustomDomain, setIsCustomDomain] = useState(false);
  const [customDomainBookData, setCustomDomainBookData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkForCustomDomain();
  }, []);

  const checkForCustomDomain = async () => {
    try {
      console.log('🔍 Starting custom domain check...');
      
      // iOS FIX: Check both window object AND sessionStorage
      let bookDataFromBackend = window.__CUSTOM_DOMAIN_BOOK__;
      
      if (!bookDataFromBackend) {
        console.log('⚠️ No window.__CUSTOM_DOMAIN_BOOK__, checking sessionStorage...');
        try {
          const stored = sessionStorage.getItem('__CUSTOM_DOMAIN_BOOK__');
          if (stored) {
            bookDataFromBackend = JSON.parse(stored);
            console.log('✅ Recovered book data from sessionStorage');
          }
        } catch (e) {
          console.warn('SessionStorage read failed:', e);
        }
      }
      
      // CHECK IF BACKEND ALREADY FOUND THE BOOK
      if (bookDataFromBackend) {
        console.log('✅ Backend provided book data:', bookDataFromBackend);
        
        console.log('📖 Fetching full book document with ID:', bookDataFromBackend.id);
        
        // Get the full book data using the ID from backend
        const bookDoc = await getDoc(doc(db, 'pb-books', bookDataFromBackend.id));
        
        console.log('📄 Book document exists:', bookDoc.exists());
        
        if (bookDoc.exists()) {
          const bookData = { id: bookDoc.id, ...bookDoc.data() };
          
          console.log('📚 Full book data retrieved:', bookData);
          
          setIsCustomDomain(true);
          setCustomDomainBookData(bookData);
          
          console.log('🎯 State updated - isCustomDomain:', true, 'bookData:', bookData);
          
          // Update document title and meta tags
          document.title = bookData.botName || 'ParrotPages Book';
          updateMetaTags(bookData);
          
          console.log('🎉 Custom domain book loaded from backend:', {
            domain: bookData.customDomain,
            path: bookData.customPath || '(no path)',
            bookName: bookData.botName
          });
          
          setLoading(false);
          return;
        } else {
          console.error('❌ Book document does not exist for ID:', bookDataFromBackend.id);
          setError('Book not found in database');
        }
      } else {
        console.log('🔍 No backend book data found, doing manual lookup...');
        const domain = window.location.hostname;
        const isCustom = !domain.includes('localhost') && 
                        !domain.includes('127.0.0.1') &&
                        !domain.includes('firebase') && 
                        domain !== 'parrotpages.co.za' &&
                        !domain.includes('web.app') &&
                        !domain.includes('firebaseapp.com');

        console.log('Domain check:', { domain, isCustom });

        if (isCustom) {
          const booksQuery = query(
            collection(db, 'pb-books'),
            where('customDomain', '==', domain),
            where('published', '==', true),
            limit(1)
          );

          const snapshot = await getDocs(booksQuery);
          
          if (!snapshot.empty) {
            const bookDoc = snapshot.docs[0];
            const bookData = { id: bookDoc.id, ...bookDoc.data() };
            
            setIsCustomDomain(true);
            setCustomDomainBookData(bookData);
            
            // Update document title and meta tags
            document.title = bookData.botName || 'ParrotPages Book';
            updateMetaTags(bookData);
            
            console.log('✅ Manual lookup successful:', bookData.botName);
            
          } else {
            setError(`No book found for domain: ${domain}`);
          }
        }
      }
    } catch (error) {
      console.error('💥 Error in checkForCustomDomain:', error);
      setError('Failed to load content: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateMetaTags = (book) => {
    const updateMeta = (selector, content) => {
      const meta = document.querySelector(selector);
      if (meta) meta.setAttribute('content', content);
    };

    updateMeta('meta[property="og:title"]', book.botName);
    updateMeta('meta[property="og:description"]', book.botCategory || 'Interactive Learning Content');
    updateMeta('meta[property="og:url"]', window.location.href);
    updateMeta('meta[name="description"]', book.botCategory || 'Interactive Learning Content');
    
    if (book.ogImage) {
      updateMeta('meta[property="og:image"]', book.ogImage);
      updateMeta('meta[name="twitter:image"]', book.ogImage);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-100">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Error</h1>
          <p className="text-xl text-gray-600 mb-8">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // REGULAR APP: Standard routing
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <CartProvider>
            <BookingProvider>
            {/* ADD CUSTOM DOMAIN CHECK HERE - INSIDE AUTHPROVIDER */}
            {isCustomDomain && customDomainBookData ? (
              <Routes>
              {/* Lightweight auth pages without Navbar */}
              <Route
                path="/login"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <Login />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <Signup />
                  </ProtectedRoute>
                }
              />

              {/* Everything else -> custom domain book */}
              <Route
                path="/*"
                element={
                  <CustomDomainBookViewer
                    bookData={customDomainBookData}
                    bookId={customDomainBookData.id}
                  />
                }
              />
            </Routes>
              
            ) : (
              <div className="min-h-screen bg-gray-50">
                
                <Routes>
                  <Route path="/" element={<><Navbar /><Home /></>} />
                  <Route path="/login" element={<><Navbar /><ProtectedRoute requireAuth={false}><Login /></ProtectedRoute></>} />
                  <Route path="/signup" element={<><Navbar /><ProtectedRoute requireAuth={false}><Signup /></ProtectedRoute></>} />
                  <Route path="/profile" element={<><Navbar /><ProtectedRoute><ProfilePage /></ProtectedRoute></>} />
                  <Route path="/organisations" element={<><Navbar /><ProtectedRoute><OrganisationPage /></ProtectedRoute></>} />
                  <Route path="/dashboard" element={<><Navbar /><ProtectedRoute><Dashboard /></ProtectedRoute></>} />
                  <Route path="/create" element={<><Navbar /><ProtectedRoute><BookCreator /></ProtectedRoute></>} />
                  <Route path="/books/:bookId/edit" element={<ProtectedRoute><BookEditor /></ProtectedRoute>} />
                  <Route path="/books/:bookId" element={<BookViewerWithConditionalNavbar />} />
                  <Route path="/provider" element={<><Navbar /><ProtectedRoute><ProviderDashboard /></ProtectedRoute></>} />
                  <Route path="/provider/dashboard" element={<><Navbar /><ProtectedRoute><ProviderDashboard /></ProtectedRoute></>} />
                  <Route path="/provider/notifications" element={<><Navbar /><ProtectedRoute><NotificationDashboard /></ProtectedRoute></>} />
                  <Route path="/provider/profile" element={<><Navbar /><ProtectedRoute><ProviderProfile /></ProtectedRoute></>} />
                  <Route path="/provider/requests" element={<><Navbar /><ProtectedRoute><ProviderRequests /></ProtectedRoute></>} />
                  <Route path="/provider/bookings" element={<><Navbar /><ProtectedRoute><ProviderBookings /></ProtectedRoute></>} />
                </Routes>
              </div>
            )}
            </BookingProvider>
          </CartProvider>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

const BookViewerWithConditionalNavbar = () => {
  const { bookId } = useParams();
  
  return (
    <BookAccessGuard bookId={bookId}>
      <SmartBookViewer />
    </BookAccessGuard>
  );
};

const SmartBookViewer = ({ bookData, bookId }) => {
  const isPublicBook = bookData?.accessLevel === 'public';
  
  return (
    <>
      {!isPublicBook && <Navbar />}
      <BookViewer 
        bookData={bookData}
        bookId={bookId}
        isPublicBook={isPublicBook}
      />
    </>
  );
};

export default App;